import { MARKER_LAYERS } from '@weenat/client/dist/core/measurements'
import { fromColorPathToColor, WeenatColor } from '@weenat/theme'
import TextNumber from 'app/src/kit/TextNumber'
import { readableColor } from 'polished'
import { HTMLProps, memo, ReactNode } from 'react'
import { styled } from 'styled-components'

const MIN_SIZE = 108
const HALO_SIZE = 160

interface InnerCircleProps {
  $size: number
  $color: string
}

interface OuterRingProps {
  $size: number
  $color: string
}

/**
 * Outer wrapper that repositions the bottom-center of the marker stack to the lat/lng coordinate.
 * Google Maps places a child's top-left corner at the coordinate by default.
 */
const ClusterContainer = styled(Flex).attrs<{ $zIndex: number | undefined; $outerSize: number }>(
  ({ $zIndex }) => ({
    style: { '--marker-z-index': $zIndex } as React.CSSProperties
  })
)`
  position: relative;
  width: ${(p) => p.$outerSize}px;
  height: ${(p) => p.$outerSize}px;

  transform: translate(-50%, -50%);
  z-index: var(--marker-z-index);

  align-items: center;
  justify-content: center;
  pointer-events: none;

  &:has(:hover) {
    z-index: ${MARKER_LAYERS.FOCUSED};
  }
`

// .attrs() resolves the WeenatColor path to a hex value and writes it as --cluster-bg-color.
// Without this, styled-components generates a new class every time the metric/value changes
// (i.e. on every sensor data refresh for every cluster on the map).
const OuterRing = styled.div.attrs<OuterRingProps>(({ $color }) => ({
  style: { '--cluster-bg-color': fromColorPathToColor($color) } as React.CSSProperties
}))<OuterRingProps>`
  position: absolute;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: ${(p) => p.theme.radiuses.rounded}px;
  background-color: var(--cluster-bg-color);
  opacity: 0.25;

  pointer-events: none;
`

// Same rationale as OuterRing: --cluster-bg-color avoids class-per-color churn.
const InnerCircle = styled(Flex).attrs<InnerCircleProps>(({ $color }) => ({
  style: { '--cluster-bg-color': fromColorPathToColor($color) } as React.CSSProperties
}))<InnerCircleProps>`
  position: relative;
  z-index: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: ${(p) => p.theme.radiuses.rounded}px;
  background-color: var(--cluster-bg-color);
  border: 2px solid ${(p) => p.theme.colors.alpha.white[30]};
  pointer-events: auto;

  &:hover {
    border-color: ${(p) => p.theme.colors.alpha.white[70]};
    cursor: pointer;
  }

  &:hover ${OuterRing} {
    transform: scale(1.1);
  }

  transition:
    0.3s ease-out transform,
    0.3s linear border-color;
`

interface PureClusterProps {
  color?: WeenatColor
  density: number
  children?: ReactNode
  /**
   * For google maps to place the item correctly on the map
   * It's optional as we may have wrappers on top of this component
   */
  lat?: number
  /**
   * For google maps to place the item correctly on the map
   * It's optional as we may have wrappers on top of this component
   */
  lng?: number
  onPress?: HTMLProps<HTMLDivElement>['onClick']
  onMouseEnter?: HTMLProps<HTMLDivElement>['onMouseEnter']
  onMouseLeave?: HTMLProps<HTMLDivElement>['onMouseLeave']
  zIndex?: number
}

const PureCluster = ({
  color = 'grayscale.black',
  density,
  children,
  lat: _lat,
  lng: _lng,
  onPress,
  onMouseEnter,
  onMouseLeave,
  zIndex
}: PureClusterProps) => {
  const innerSize = MIN_SIZE
  const outerSize = HALO_SIZE

  return (
    <ClusterContainer $outerSize={outerSize} $zIndex={zIndex}>
      <OuterRing $size={outerSize} $color={color} />
      <InnerCircle
        $size={innerSize}
        $color={color}
        onClick={onPress}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <TextNumber
          $fontSize='md'
          $fontWeight='bold'
          $color={readableColor(fromColorPathToColor(color))}
          $textAlign='center'
        >
          {density}
        </TextNumber>
        {children}
      </InnerCircle>
    </ClusterContainer>
  )
}

export default memo(PureCluster)
